import { useLayoutEffect, useRef } from "react";

const MIN_ROW_VIEWPORT_HEIGHT = 300;
const VIEWPORT_BOTTOM_GAP = 8;
const FALLBACK_GRID_HEIGHT = 500;

const useViewportFit = (enabled: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container) {
      return;
    }

    const updateHeight = () => {
      const viewportHeight =
        window.visualViewport?.height ?? window.innerHeight;
      // Document offset keeps the height stable while the page scrolls.
      const top = container.getBoundingClientRect().top + window.scrollY;
      const availableHeight = viewportHeight - top - VIEWPORT_BOTTOM_GAP;
      const gridRoot =
        container.querySelector<HTMLElement>(".MuiDataGrid-root");
      const rowViewport = container.querySelector<HTMLElement>(
        ".MuiDataGrid-virtualScroller"
      );
      const gridChromeHeight =
        gridRoot && rowViewport
          ? Math.max(
              0,
              gridRoot.getBoundingClientRect().height -
                rowViewport.getBoundingClientRect().height
            )
          : FALLBACK_GRID_HEIGHT - MIN_ROW_VIEWPORT_HEIGHT;
      const minimumGridHeight = gridChromeHeight + MIN_ROW_VIEWPORT_HEIGHT;
      const height = `${Math.max(minimumGridHeight, availableHeight)}px`;

      if (
        container.style.getPropertyValue("--data-grid-viewport-fit-height") !==
        height
      ) {
        container.style.setProperty("--data-grid-viewport-fit-height", height);
      }
    };

    let frame: number | undefined;
    const scheduleUpdate = () => {
      if (frame !== undefined) {
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = undefined;
        updateHeight();
      });
    };

    updateHeight();

    // Size changes only; scrolling never triggers these.
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(scheduleUpdate);
    const observed = [
      document.body,
      container,
      container.querySelector<HTMLElement>(".MuiDataGrid-root"),
      container.querySelector<HTMLElement>(".MuiDataGrid-virtualScroller"),
    ];
    for (const element of observed) {
      if (element) {
        resizeObserver?.observe(element);
      }
    }
    window.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("resize", scheduleUpdate);

    return () => {
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
      }
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("resize", scheduleUpdate);
      container.style.removeProperty("--data-grid-viewport-fit-height");
    };
  }, [enabled]);

  return containerRef;
};

export { useViewportFit };
